import { useEffect, useState } from 'react';

import { groupBy } from 'lodash';
import { useSnapshot } from 'valtio';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';

import { BaseSelectFilter } from '@/components/common/BaseSelectFilter';

import { useGetRefrigerationUnitModelsQuery } from '@/graphql/GetRefrigerationUnitModels.generated';

import { appStore, reportRequestError, retryAfterRequestError } from '@/store';

import {
  RefrigerationUnitModelsFilter,
  getRefrigerationUnitModel,
  selectRefrigerationUnitModels,
  setRefrigerationUnitModels,
  setSelectedRefrigerationUnitModels,
  store,
  unselectAllRefrigerationUnitModels,
  unselectRefrigerationUnitModel,
  unselectRefrigerationUnitModels,
} from '../store';

function RefrigerationUnitModelsSelectFilter() {
  const appSnap = useSnapshot(appStore);
  const snap = useSnapshot(store);

  const [{ data, fetching, error }, reexecuteQuery] =
    useGetRefrigerationUnitModelsQuery({
      pause: snap.refrigerationUnitModels.length > 0,
    });

  const [errorId, setErrorId] = useState<string | null>(null);

  useEffect(() => {
    if (data && data.container_insights_refrigeration_unit_models) {
      setRefrigerationUnitModels(
        data.container_insights_refrigeration_unit_models,
      );
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      setErrorId(reportRequestError(error, reexecuteQuery));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const handleRetry = () => {
    retryAfterRequestError(errorId);
    setErrorId(null);
  };

  const handleRemove = (id: string) => () => unselectRefrigerationUnitModel(id);

  const handleSelectGroup = (models: RefrigerationUnitModelsFilter[]) => () => {
    const ids = models.map((model) => model.id);
    selectRefrigerationUnitModels(ids);
  };

  const handleClearGroup = (models: RefrigerationUnitModelsFilter[]) => () => {
    const ids = models.map((model) => model.id);
    unselectRefrigerationUnitModels(ids);
  };

  const selectedModelsIds = snap.selectedRefrigerationUnitModels.map(
    (model) => model.id,
  );
  const groups = groupBy(snap.refrigerationUnitModels, 'manufacturer.name');

  return (
    <BaseSelectFilter
      id="models-select-filter"
      label="Models"
      values={selectedModelsIds}
      onChange={setSelectedRefrigerationUnitModels}
      onClear={unselectAllRefrigerationUnitModels}
      loading={fetching}
      error={
        appSnap.hasRequestError(errorId) ? 'Error while fetching models' : null
      }
      onRetry={handleRetry}
      maxHeight={48 * 8 + 8}
      renderChips={(selected: string[]) =>
        selected
          .map(getRefrigerationUnitModel)
          .filter((model) => model !== null)
          .map((model) => (
            <Chip
              key={model.id}
              size="small"
              color="primary"
              variant="outlined"
              label={model.name}
              onDelete={handleRemove(model.id)}
            />
          ))
      }
    >
      {Object.entries(groups).map(([group, models]) => {
        const allSelected = models.every((model) => model.selected);
        return [
          <ListSubheader
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <Box sx={{ flexGrow: 1 }}>
              {group}
              <sup
                style={{
                  marginLeft: 4,
                  visibility: models.some((model) => model.selected)
                    ? 'visible'
                    : 'hidden',
                }}
              >
                ({models.filter((model) => model.selected).length})
              </sup>
            </Box>
            <Button
              size="small"
              sx={{ textTransform: 'none' }}
              onClick={
                allSelected
                  ? handleClearGroup(models as RefrigerationUnitModelsFilter[])
                  : handleSelectGroup(models as RefrigerationUnitModelsFilter[])
              }
            >
              {allSelected ? 'Clear' : 'Select all'}
            </Button>
          </ListSubheader>,
          models
            .filter((t) => typeof t !== 'undefined')
            .map((model) => (
              <MenuItem key={model.id} value={model.id}>
                <Checkbox checked={model.selected} />
                <ListItemText primary={model.name} />
              </MenuItem>
            )),
        ];
      })}
    </BaseSelectFilter>
  );
}

export { RefrigerationUnitModelsSelectFilter };
